import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { batch1En, batch1Tl } from "./i18n.batch1";
import { batch2En, batch2Tl } from "./i18n.batch2";
import { batch3En, batch3Tl } from "./i18n.batch3";
import { batch4En, batch4Tl } from "./i18n.batch4";
import { finalEn, finalTl } from "./i18n.final";
import { rewardsEn, rewardsTl } from "./i18n.rewards";

/**
 * Multilingual labels for AJ's Farm Enterprise Management System.
 * Supports English (en) and Filipino / Tagalog (tl).
 *
 * Employees pick their preferred language from the profile menu; the choice is
 * persisted to their user record via PUT /api/auth/language AND to localStorage
 * so the UI switches immediately without another round-trip.
 *
 * Add new keys by extending EN first, then TL. Missing keys fall back to EN.
 * Additional languages can be added later without changing page architecture.
 * (see i18next `fallbackLng: 'en'`).
 */

export const en = {
    rewards: rewardsEn,
    breakEven: {
        partialTitle: "Partial cost coverage.", partialMessage: "Transport, utilities, and other per-unit costs are unavailable, so these figures are indicative and must not be treated as complete.",
        knownVariableCost: "Known Variable Cost / Unit", indicativeBreakEven: "Indicative Break-Even", indicativeResult: "Indicative Profit / Loss", profit: "Profit", loss: "Loss",
    },
    ai_katulong: {
        title: "AI Katulong", subtitle: "Your intelligent AJ's Farm assistant", read_only: "Read-only farm decision support",
        new_conversation: "New Conversation", send: "Send", retry: "Retry", placeholder: "Ask AI Katulong about authorized farm information…",
        empty_title: "How can I help your farm today?", empty_help: "Choose a starter action or ask a question. Farm-specific answers use only data your account is authorized to access.",
        unavailable: "AI Katulong is temporarily unavailable", unavailable_help: "The secure provider is not configured or cannot be reached. AJ's Farm remains fully usable.",
        loading: "Reviewing authorized farm information…", advisory: "AI advice is advisory. Verify important operational, financial, and animal-health decisions.",
        status_unavailable: "AI status is unavailable", explain_dashboard: "Explain Dashboard",
        starters: {
            dashboard: "Explain my dashboard", attention: "What needs my attention today?", farm: "Summarize my farm",
            livestock: "Check livestock", inventory: "Check inventory", tasks: "Check today's tasks",
            breeding: "Check breeding and gestation", finance: "Check sales and expenses", attendance: "Check attendance",
            events: "Check upcoming farm events", workforce: "Explain workforce and earnings",
        },
        capabilities: { general: "General Farm Assistant", farm_summary: "Farm performance summary", growth_observation: "Growth and weight observations", feed_inventory: "Feed and inventory warnings", expense_review: "Expense review", workforce_earnings: "Workforce & Earnings", task_priority: "My Tasks & Priorities", health_observation: "Health observation support", breeding_insight: "Breeding and gestation insights", breeding_gestation_summary: "Breeding and gestation summary", livestock_summary: "Livestock summary" },
        no_authorized_capabilities: "No AI capability is authorized for your account. Contact an administrator if you need access.",
    },
    batch1: batch1En,
    batch2: batch2En,
    batch3: batch3En,
    batch4: batch4En,
    final: finalEn,
    languages: { en: "English", tl: "Filipino / Tagalog" },
    settings: {
        language_title: "Language",
        language_help: "Choose the language used by your account. Changes apply immediately and remain after your next login.",
    },
    profile: {
        title: "Profile", employee: "Employee", role: "Role", email: "Email",
        position: "Position", department: "Department", assigned_area: "Assigned area",
        shift_status: "Shift status", today_tasks: "Today's tasks",
        salary_private: "Salary data is not shown here.", my_attendance: "My Attendance",
        attendance_help: "Open your monthly attendance calendar and daily details (Asia/Manila).",
        my_payroll: "My Payroll",
        payroll_help: "Open your timesheet, overtime requests, payslips, and payroll summary.",
    },
    login: {
        built_from_field: "Built from the field", suite: "Est. Farm Management Suite",
        hero_line_1: "Manage your farm. Track progress.", hero_line_2: "Grow your future.",
        hero_full: "Manage your farm. Track progress. Grow your future.",
        legacy_statement: "Built with sacrifice. Strengthened by hard work. Grown with patience.",
        sign_in: "Sign In", create_account: "Create Account", welcome_back: "Welcome Back!",
        join_farm: "Join AJ's Farm", join_help: "Join the farm team and start tracking today.",
        reset_success: "Password reset successfully. Please sign in with your new password.",
        full_name: "Full Name", password: "Password", forgot_password: "Forgot password?",
        signing_in: "Signing in…", log_in: "Log in", new_here: "New here?", sign_up: "Sign up",
        have_account: "Already have an account?", designed_in_field: "Designed and built in the field",
    },
    access: {
        denied: "Access Denied", owner_only: "Owner-Only",
        buyer_restricted: "Buyer/Customer accounts cannot access internal farm-management workflows.",
        tasks_restricted: "Your role cannot access employee task assignments.",
        area_restricted: "Your role cannot access this farm-management area.",
        owner_restricted: "This section is restricted to the farm owner.",
        no_permission: "You don't have permission to view this page.",
        field_restricted: "Your role cannot access Field Mode.",
    },
    alerts: {
        count_one: "{{count}} alert", count_other: "{{count}} alerts", all_clear: "All clear",
        summary: "{{total}} total · {{urgent}} urgent", none: "🎉 Nothing needs your attention right now.",
        modules: { fleet: "Fleet", equipment: "Machinery", inventory: "Inventory", aquaculture: "Aquaculture", incidents: "Incidents", admin: "Admin" },
    },
    attendance: {
        status: { not_started: "Not Started", shift_started: "Shift Started", working: "Working", on_break: "On Break", clocked_out: "Clocked Out", finished: "Finished" },
        actions: { shift_start: "Shift Start", clock_in: "Clock In", morning_break: "Start Morning Break (Paid)", lunch: "Start Lunch (Unpaid)", afternoon_break: "Start Afternoon Break (Paid)", break_end: "Break End", clock_out: "Clock Out", shift_end: "Shift End" },
    },
    employee_home: {
        title: "Employee Home", weather_summary: "Weather Summary",
        weather_temperature: "{{temperature}}°C, feels like {{feels}}°C",
        weather_detail: "Humidity {{humidity}}% · Wind {{wind}} km/h",
        weather_unavailable: "Weather unavailable", weather_location_unavailable: "Location data unavailable or weather service is unreachable.",
        fetching_weather: "Fetching latest weather…", urgent_alerts: "Urgent Alerts",
        complete_percent: "Complete {{percent}}%", clocking: "Clock In / Clock Out",
        controlled_time: "Controlled test timing is active for this dedicated test account. Each normal clock action uses the approved Asia/Manila reference schedule.",
        not_linked: "Employee profile is not linked to this account", clock_failed: "Clock action failed",
    },
    offline: {
        status: { online: "ONLINE — SYNCED", offline: "OFFLINE — SAVED ON THIS DEVICE", syncing: "SYNCING...", pending: "SYNC PENDING", failed: "SYNC FAILED — RETRYING", review: "REVIEW REQUIRED" },
        summary: "{{pending}} pending · {{failed}} failed · {{review}} review required",
        photos_pending: "{{count}} photos pending",
        last_synced: "Last synced {{time}}", retry: "Retry Sync",
        saved_device: "Saved safely on this device — sync pending",
        internet_required: "Internet connection required for this action.",
        owner: { eyebrow: "Offline operations", title: "Sync Monitor", help: "Review safe synchronization health and conflicts without exposing device secrets.", conflicts: "Conflicts requiring review", none: "No conflicts require review.", reason: "Required decision reason", keep_server: "Keep server record", reject: "Reject operation", retry: "Retry after review" },
        capture: { eyebrow: "Offline field capture", title: "Record Field Work", incident: "Incident", inventory: "Inventory Use", form: "Form", incident_title: "Incident title", details: "Details", choose_item: "Choose an authorized item", quantity: "Quantity used", reason: "Reason for use", choose_form: "Choose a form", save: "Save on this device", notice: "The server rechecks your identity and permissions before applying this change.", title_required: "Incident title is required", inventory_required: "Choose an item and enter a valid quantity", form_required: "Choose a form" },
    },
    workforce_analytics: {
        eyebrow: "Owner analytics", title: "Workforce & Earnings Analytics",
        subtitle: "Farm-wide labour, productivity, revenue, and net contribution from existing attendance, payroll, task, and financial records.",
        attribution_notice: "Revenue and net contribution are farm-wide. They are not attributed to individual employees or departments unless a verified source relationship exists.",
        department_notice: "Department results show labour hours and labour cost only; they do not claim employee-generated revenue.",
        not_available: "Not available",
        period: { label: "Reporting period", today: "Today", week: "This Week", month: "This Month", year: "This Year", custom: "Custom", from: "From", to: "To" },
        sections: { glance: "Workforce at a Glance", contribution: "Workforce Contribution", productivity: "Productivity", departments: "Department Labour" },
        metrics: { active_workers: "Active Employees", present_today: "Present Today", labour_hours: "Labour Hours", regular_hours: "Regular Hours", overtime_hours: "Overtime", labour_cost: "Labour Cost", labour_cost_note: "Posted payroll when available; otherwise canonical accrued estimate", revenue: "Revenue", other_costs: "Other Costs", net_contribution: "Net Earnings", farm_wide: "Farm-wide", tasks_completed: "Tasks Completed", completion_rate: "Completion Rate", revenue_per_hour: "Revenue per Labour Hour", labour_cost_per_task: "Labour Cost per Task" },
        charts: { contribution: "Revenue, Labour Cost & Net Contribution", labour: "Labour Hours Trend" },
        errors: { owner_only: "Only the Owner/Admin can view workforce earnings analytics.", load: "Workforce analytics could not be loaded." },
    },
    groups: {
        overview: "Overview", livestock: "Livestock", crops: "Crops",
        sales: "Sales & POS", finance: "Inventory & Finance", facility: "Facility",
        daily_ops: "Daily Ops", system: "System",
    },
    nav: {
        home: "Home", profile: "Profile", more: "More", primary_mobile: "Primary mobile navigation", toggle_group: "Toggle {{group}} navigation group", overview: "Overview", dashboard: "Dashboard", alerts: "Alerts",
        livestock_hub: "Livestock Hub",
        species: "Species", birth_to_sale: "Birth-to-Sale",
        growth: "Growth & FCR", break_even: "Break-Even",
        crops: "Crops & Fields", aquaculture: "Aquaculture",
        construction: "Construction", assets: "Assets",
        fleet: "Fleet Management",
        equipment: "Machinery & Equipment",
        financial: "Financial", workforce_analytics: "Workforce & Earnings", payroll: "Payroll (PH)", reports: "Reports",
        feed_inventory: "Feed Inventory", purchasing: "Purchasing", inventory: "Inventory", batches: "Product Batches",
        sales_inventory: "Sales & Inventory", sales_dashboard: "Sales Dashboard",
        sales_targets: "Sales Targets",
        suppliers: "Suppliers", purchase_orders: "Purchase Orders",
        calendar: "Calendar", tasks: "Tasks", reminders: "Reminders",
        pos: "POS · Sell Now", shifts: "Shifts · Cash Drawer",
        customers: "Customers", deliveries: "Deliveries",
        field_mode: "Field Mode", scan: "QR Scanner", worker_tasks: "My Tasks",
        forms: "Forms & Assessments",
        incidents: "Incident Reports",
        employees: "Employees", attendance: "Attendance", hr: "HR & Training",
        assistant: "AI Katulong", notifications: "Notifications",
        admin: "Admin Settings",
        admin_users: "Admin Dashboard", sync_monitor: "Sync Monitor", recycle_bin: "Recycle Bin",
    },
    field: {
        title: "Field Mode",
        pick_animal: "Pick animal", pick_action: "What are you doing?",
        feed: "Feed", water: "Water", vaccinate: "Vaccinate",
        weigh: "Weigh", treat: "Treat", birth: "Birth", mortality: "Mortality",
        clean: "Clean", inspect: "Inspect", harvest: "Harvest",
        plant: "Plant", spray: "Spray", irrigate: "Irrigate",
        repair: "Repair", maintenance: "Maintenance", approve: "Approve",
        submit: "Submit", cancel: "Cancel", notes_placeholder: "Notes (tap 🎤 to dictate)",
        no_actions: "No Field Mode actions are enabled for your role. Ask the owner to grant access from Admin Settings.",
    },
    common: {
        save: "Save", delete: "Delete", edit: "Edit", cancel: "Cancel", loading: "Loading…",
        yes: "Yes", no: "No", search: "Search", filter: "Filter", export: "Export",
        import: "Import", refresh: "Refresh", back: "Back", next: "Next",
        language: "Language", saving: "Saving…", open: "Open", today: "Today", date: "Date", status: "Status", updated: "Updated",
        no_data: "No data", not_assigned: "Not assigned", logout: "Logout", loading_farm: "Loading farm…",
    },
    audit: {
        title: "Audit Log", timestamp: "Time", user: "User", action: "Action", summary: "Summary",
        collection: "Section",
    },
    backup: {
        title: "Backup & Restore",
        export_btn: "Export Settings",
        import_btn: "Import Settings",
        import_hint: "Owner-only. Import merges by ID (existing entries are updated, new entries created).",
        drop_file: "Drop a settings JSON file here or click to browse.",
    },
    field_mode_access: {
        title: "Field Mode Access",
        subtitle: "Choose which Field Mode actions each role can perform.",
        role: "Role",
    },
    forms: {
        title: "Assessments & Cleaning Forms",
        subtitle: "25 built-in templates (11 pen assessments + 14 cleaning). Fully editable.",
        category_pen: "Pen Assessments",
        category_cleaning: "Cleaning Forms",
        category_custom: "Custom",
        new_template: "New Template",
        submissions: "Submissions",
    },
    fleet: {
        subtitle: "Every farm vehicle · fuel · maintenance · compliance · inspections.",
    },
};

export const tl = {
    rewards: rewardsTl,
    breakEven: {
        partialTitle: "Bahagya lamang ang saklaw ng gastos.", partialMessage: "Hindi makuha ang gastos bawat yunit para sa transportasyon, utilities, at iba pang gastos, kaya tantiya lamang ang mga resultang ito at hindi dapat ituring na kumpleto.",
        knownVariableCost: "Kilala na Gastos Bawat Yunit", indicativeBreakEven: "Tantiyang Break-Even", indicativeResult: "Tantiyang Tubo / Lugi", profit: "Tubo", loss: "Lugi",
    },
    ai_katulong: {
        title: "AI Katulong", subtitle: "Ang matalinong katuwang mo sa AJ's Farm", read_only: "Read-only na suporta sa pagpapasya sa bukid",
        new_conversation: "Bagong Usapan", send: "Ipadala", retry: "Subukan Muli", placeholder: "Magtanong sa AI Katulong tungkol sa awtorisadong impormasyon ng bukid…",
        empty_title: "Paano ako makatutulong sa bukid ngayon?", empty_help: "Pumili ng panimulang tanong o magtanong. Gumagamit lamang ang sagot ng datos na awtorisadong makita ng iyong account.",
        unavailable: "Pansamantalang hindi available ang AI Katulong", unavailable_help: "Hindi naka-configure o hindi maabot ang secure provider. Magagamit pa rin nang buo ang AJ's Farm.",
        loading: "Sinusuri ang awtorisadong impormasyon ng bukid…", advisory: "Payo lamang ang sagot ng AI. Beripikahin ang mahahalagang desisyon sa operasyon, pananalapi, at kalusugan ng hayop.",
        status_unavailable: "Hindi makuha ang status ng AI", explain_dashboard: "Ipaliwanag ang Dashboard",
        starters: {
            dashboard: "Ipaliwanag ang aking dashboard", attention: "Ano ang dapat kong tutukan ngayon?", farm: "Ibuod ang aking bukid",
            livestock: "Suriin ang mga alagang hayop", inventory: "Suriin ang inventory", tasks: "Suriin ang mga gawain ngayong araw",
            breeding: "Suriin ang breeding at gestation", finance: "Suriin ang benta at gastos", attendance: "Suriin ang attendance",
            events: "Suriin ang mga paparating na event sa bukid", workforce: "Ipaliwanag ang workforce at earnings",
        },
        capabilities: { general: "Pangkalahatang Katulong sa Bukid", farm_summary: "Buod ng pagganap ng bukid", growth_observation: "Obserbasyon sa paglaki at timbang", feed_inventory: "Babala sa pakain at imbentaryo", expense_review: "Pagsusuri ng gastos", workforce_earnings: "Lakas-Paggawa at Kita", task_priority: "Aking mga Gawain at Prayoridad", health_observation: "Suporta sa obserbasyon ng kalusugan", breeding_insight: "Pagsusuri sa pagpapalahi at pagbubuntis", breeding_gestation_summary: "Buod ng pagpapalahi at pagbubuntis", livestock_summary: "Buod ng mga alagang hayop" },
        no_authorized_capabilities: "Walang AI capability na pinahihintulutan para sa iyong account. Makipag-ugnayan sa administrator kung kailangan mo ng access.",
    },
    batch1: batch1Tl,
    batch2: batch2Tl,
    batch3: batch3Tl,
    batch4: batch4Tl,
    final: finalTl,
    languages: { en: "English", tl: "Filipino / Tagalog" },
    settings: {
        language_title: "Wika",
        language_help: "Piliin ang wikang gagamitin ng iyong account. Agad itong ilalapat at mananatili sa susunod mong pag-login.",
    },
    profile: {
        title: "Profile", employee: "Empleyado", role: "Tungkulin", email: "Email",
        position: "Posisyon", department: "Departamento", assigned_area: "Nakatakdang lugar",
        shift_status: "Katayuan ng shift", today_tasks: "Mga gawain ngayong araw",
        salary_private: "Hindi ipinapakita rito ang datos ng sahod.", my_attendance: "Aking Attendance",
        attendance_help: "Buksan ang buwanang attendance calendar at mga detalye bawat araw (Asia/Manila).",
        my_payroll: "Aking Payroll",
        payroll_help: "Buksan ang timesheet, overtime request, payslip, at buod ng payroll.",
    },
    login: {
        built_from_field: "Binuo mula sa bukid", suite: "Farm Management Suite",
        hero_line_1: "Pamahalaan ang bukid. Subaybayan ang progreso.", hero_line_2: "Palaguin ang iyong kinabukasan.",
        hero_full: "Pamahalaan ang bukid. Subaybayan ang progreso. Palaguin ang iyong kinabukasan.",
        legacy_statement: "Binuo sa sakripisyo. Pinatatag ng sipag. Pinalago nang may tiyaga.",
        sign_in: "Mag-sign In", create_account: "Gumawa ng Account", welcome_back: "Maligayang pagbabalik!",
        join_farm: "Sumali sa AJ's Farm", join_help: "Sumali sa farm team at magsimulang magtala ngayon.",
        reset_success: "Matagumpay na na-reset ang password. Mag-sign in gamit ang bago mong password.",
        full_name: "Buong Pangalan", password: "Password", forgot_password: "Nakalimutan ang password?",
        signing_in: "Nagsa-sign in…", log_in: "Mag-log in", new_here: "Bago rito?", sign_up: "Mag-sign up",
        have_account: "May account ka na?", designed_in_field: "Dinisenyo at binuo sa bukid",
    },
    access: {
        denied: "Hindi Pinapayagan", owner_only: "Para sa Owner Lamang",
        buyer_restricted: "Hindi maaaring pumasok ang Buyer/Customer account sa mga panloob na gawain ng bukid.",
        tasks_restricted: "Hindi maaaring tingnan ng iyong tungkulin ang mga gawaing nakatalaga sa empleyado.",
        area_restricted: "Hindi maaaring pumasok ang iyong tungkulin sa bahaging ito ng farm management.",
        owner_restricted: "Ang seksyong ito ay para lamang sa may-ari ng bukid.",
        no_permission: "Wala kang pahintulot na tingnan ang pahinang ito.",
        field_restricted: "Hindi maaaring pumasok ang iyong tungkulin sa Field Mode.",
    },
    alerts: {
        count_one: "{{count}} babala", count_other: "{{count}} babala", all_clear: "Walang kailangang asikasuhin",
        summary: "{{total}} lahat · {{urgent}} apurahan", none: "🎉 Walang kailangang bigyan ng pansin ngayon.",
        modules: { fleet: "Mga Sasakyan", equipment: "Makinarya", inventory: "Imbentaryo", aquaculture: "Aquaculture", incidents: "Mga Insidente", admin: "Admin" },
    },
    attendance: {
        status: { not_started: "Hindi pa Nagsisimula", shift_started: "Nagsimula ang Shift", working: "Nagtatrabaho", on_break: "Naka-break", clocked_out: "Naka-clock Out", finished: "Tapos na" },
        actions: { shift_start: "Simulan ang Shift", clock_in: "Clock In", morning_break: "Simulan ang Morning Break (Bayad)", lunch: "Simulan ang Tanghalian (Hindi Bayad)", afternoon_break: "Simulan ang Afternoon Break (Bayad)", break_end: "Tapusin ang Break", clock_out: "Clock Out", shift_end: "Tapusin ang Shift" },
    },
    employee_home: {
        title: "Home ng Empleyado", weather_summary: "Buod ng Panahon",
        weather_temperature: "{{temperature}}°C, pakiramdam ay {{feels}}°C",
        weather_detail: "Halumigmig {{humidity}}% · Hangin {{wind}} km/h",
        weather_unavailable: "Hindi makuha ang panahon", weather_location_unavailable: "Hindi makuha ang lokasyon o hindi maabot ang weather service.",
        fetching_weather: "Kinukuha ang pinakabagong panahon…", urgent_alerts: "Apurahang Babala",
        complete_percent: "{{percent}}% kumpleto", clocking: "Clock In / Clock Out",
        controlled_time: "Aktibo ang kontroladong oras para sa nakalaang test account na ito. Ginagamit ng bawat normal na clock action ang aprubadong iskedyul ng Asia/Manila.",
        not_linked: "Hindi naka-link ang employee profile sa account na ito", clock_failed: "Hindi naisagawa ang clock action",
    },
    offline: {
        status: { online: "ONLINE — NAKA-SYNC", offline: "OFFLINE — NAKA-SAVE SA DEVICE NA ITO", syncing: "NAGSISYNC...", pending: "MAY HINDI PA NAKA-SYNC", failed: "HINDI NAKA-SYNC — MULING SUSUBUKAN", review: "KAILANGANG SURIIN" },
        summary: "{{pending}} nakabinbin · {{failed}} nabigo · {{review}} kailangang suriin",
        photos_pending: "{{count}} larawang nakabinbin",
        last_synced: "Huling sync {{time}}", retry: "Subukang I-sync",
        saved_device: "Ligtas na naka-save sa device na ito — naghihintay ng sync",
        internet_required: "Kailangan ng internet connection para sa aksyong ito.",
        owner: { eyebrow: "Mga offline na operasyon", title: "Monitor ng Sync", help: "Suriin ang kalagayan ng sync at mga conflict nang hindi inilalantad ang lihim ng device.", conflicts: "Mga conflict na kailangang suriin", none: "Walang conflict na kailangang suriin.", reason: "Kinakailangang dahilan ng desisyon", keep_server: "Panatilihin ang record sa server", reject: "Tanggihan ang operasyon", retry: "Subukang muli pagkatapos suriin" },
        capture: { eyebrow: "Offline na pagtatala sa bukid", title: "Itala ang Gawain sa Bukid", incident: "Insidente", inventory: "Gamit sa Imbentaryo", form: "Form", incident_title: "Pamagat ng insidente", details: "Mga detalye", choose_item: "Pumili ng pinapayagang item", quantity: "Daming ginamit", reason: "Dahilan ng paggamit", choose_form: "Pumili ng form", save: "I-save sa device na ito", notice: "Muling sinusuri ng server ang iyong pagkakakilanlan at pahintulot bago ilapat ang pagbabagong ito.", title_required: "Kailangan ang pamagat ng insidente", inventory_required: "Pumili ng item at maglagay ng wastong dami", form_required: "Pumili ng form" },
    },
    workforce_analytics: {
        eyebrow: "Pagsusuri ng May-ari", title: "Pagsusuri sa Lakas-Paggawa at Kita",
        subtitle: "Farm-wide na pagsusuri ng paggawa, productivity, kita, at net contribution mula sa kasalukuyang attendance, payroll, task, at financial records.",
        attribution_notice: "Farm-wide ang revenue at net contribution. Hindi ito ibinibigay na kita ng isang empleyado o departamento maliban kung may beripikadong ugnayan sa source record.",
        department_notice: "Labour hours at labour cost lamang ang ipinapakita bawat departamento; hindi nito sinasabing ang empleyado ang direktang lumikha ng revenue.",
        not_available: "Hindi available",
        period: { label: "Panahon ng ulat", today: "Ngayon", week: "Linggong Ito", month: "Buwang Ito", year: "Taong Ito", custom: "Piling Petsa", from: "Mula", to: "Hanggang" },
        sections: { glance: "Mabilisang Tingin sa Workforce", contribution: "Ambag ng Workforce", productivity: "Productivity", departments: "Paggawa Bawat Departamento" },
        metrics: { active_workers: "Aktibong Empleyado", present_today: "Pumasok Ngayon", labour_hours: "Oras ng Paggawa", regular_hours: "Regular na Oras", overtime_hours: "Overtime", labour_cost: "Gastos sa Paggawa", labour_cost_note: "Posted payroll kapag mayroon; kung wala, canonical accrued estimate", revenue: "Revenue", other_costs: "Ibang Gastos", net_contribution: "Netong Kita", farm_wide: "Buong bukid", tasks_completed: "Natapos na Gawain", completion_rate: "Completion Rate", revenue_per_hour: "Revenue Bawat Oras ng Paggawa", labour_cost_per_task: "Gastos sa Paggawa Bawat Task" },
        charts: { contribution: "Revenue, Gastos sa Paggawa at Net Contribution", labour: "Trend ng Oras ng Paggawa" },
        errors: { owner_only: "Owner/Admin lamang ang maaaring tumingin sa workforce earnings analytics.", load: "Hindi ma-load ang workforce analytics." },
    },
    groups: {
        overview: "Buod", livestock: "Alagang Hayop", crops: "Pananim",
        sales: "Benta at POS", finance: "Imbentaryo at Pananalapi", facility: "Pasilidad",
        daily_ops: "Araw-araw", system: "Sistema",
    },
    nav: {
        home: "Home", profile: "Profile", more: "Iba pa", primary_mobile: "Kunahing mobile navigation", toggle_group: "Buksan o isara ang {{group}} navigation group", overview: "Buod", dashboard: "Dashboard", alerts: "Babala",
        livestock_hub: "Sentro ng Alagang Hayop",
        species: "Uri ng Hayop", birth_to_sale: "Panganganak hanggang Benta",
        growth: "Paglaki at FCR", break_even: "Break-Even",
        crops: "Pananim at Bukirin", aquaculture: "Isda / Fishpond",
        construction: "Konstruksyon", assets: "Kagamitan",
        fleet: "Sasakyan / Fleet",
        equipment: "Makina at Kagamitan",
        financial: "Pananalapi", workforce_analytics: "Lakas-Paggawa at Kita", payroll: "Sahod (PH)", reports: "Ulat",
        feed_inventory: "Imbentaryo ng Pakain", purchasing: "Pagbili", inventory: "Imbentaryo", batches: "Mga Batch ng Produkto",
        sales_inventory: "Bentang Imbentaryo", sales_dashboard: "Sales Dashboard",
        sales_targets: "Target ng Benta",
        suppliers: "Mga Supplier", purchase_orders: "Purchase Orders",
        calendar: "Kalendaryo", tasks: "Gawain", reminders: "Paalala",
        pos: "POS · Magbenta", shifts: "Shift · Cash Drawer",
        customers: "Kustomer", deliveries: "Paghahatid",
        field_mode: "Sa Farm", scan: "QR Scanner", worker_tasks: "Aking Gawain",
        forms: "Forma at Assessment",
        incidents: "Report ng Insidente",
        employees: "Empleyado", attendance: "Attendance", hr: "HR at Training",
        assistant: "AI Katulong", notifications: "Abiso",
        admin: "Admin Settings",
        admin_users: "Admin Dashboard", sync_monitor: "Monitor ng Sync", recycle_bin: "Basurahan ng mga Record",
    },
    field: {
        title: "Field Mode",
        pick_animal: "Piliin ang hayop", pick_action: "Anong ginagawa mo?",
        feed: "Pakain", water: "Tubig", vaccinate: "Bakuna",
        weigh: "Timbang", treat: "Gamot", birth: "Panganak", mortality: "Namatay",
        clean: "Linisan", inspect: "Suriin", harvest: "Anihin",
        plant: "Magtanim", spray: "Mag-spray", irrigate: "Magpatubig",
        repair: "Ayusin", maintenance: "Maintenance", approve: "Aprubahan",
        submit: "I-submit", cancel: "Kanselahin", notes_placeholder: "Tala (pindutin ang 🎤 para magdikta)",
        no_actions: "Walang Field Mode aksyon na pinapayagan sa iyong role. Hilingin sa may-ari ang pahintulot mula sa Admin Settings.",
    },
    common: {
        save: "I-save", delete: "Tanggalin", edit: "I-edit", cancel: "Kanselahin", loading: "Naglo-load…",
        yes: "Oo", no: "Hindi", search: "Maghanap", filter: "Filter", export: "I-export",
        import: "I-import", refresh: "I-refresh", back: "Bumalik", next: "Susunod",
        language: "Wika", saving: "Sine-save…", open: "Buksan", today: "Ngayon", date: "Petsa", status: "Katayuan", updated: "Huling update",
        no_data: "Walang datos", not_assigned: "Hindi pa nakatalaga", logout: "Mag-logout", loading_farm: "Binubuksan ang bukid…",
    },
    audit: {
        title: "Audit Log", timestamp: "Oras", user: "Gumamit", action: "Aksyon", summary: "Buod",
        collection: "Seksyon",
    },
    backup: {
        title: "Backup at Restore",
        export_btn: "I-export ang Settings",
        import_btn: "I-import ang Settings",
        import_hint: "Owner lamang. Ang pag-import ay nagsasama-sama batay sa ID.",
        drop_file: "Ilagay dito ang settings JSON file o pumili.",
    },
    field_mode_access: {
        title: "Field Mode Access",
        subtitle: "Piliin kung anong aksyon ang pwedeng gawin ng bawat role sa Field Mode.",
        role: "Role",
    },
    forms: {
        title: "Forma at Assessment · Paglilinis",
        subtitle: "25 nakahandang template (11 pen assessment + 14 cleaning). Maari itong baguhin.",
        category_pen: "Pen Assessment",
        category_cleaning: "Paglilinis",
        category_custom: "Custom",
        new_template: "Bagong Template",
        submissions: "Mga Naisumite",
    },
    fleet: {
        subtitle: "Bawat sasakyan · gasolina · maintenance · compliance · inspeksyon.",
    },
};

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: { en: { translation: en }, tl: { translation: tl } },
        supportedLngs: ["en", "tl"],
        fallbackLng: "en",
        interpolation: { escapeValue: false },
        detection: {
            order: ["localStorage", "navigator"],
            lookupLocalStorage: "aj-farm-lang",
            caches: ["localStorage"],
        },
    });

export default i18n;
